import { useState } from 'react';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Sparkles, AlertTriangle, CheckCircle2, XCircle, Lightbulb, Trash2, Edit, Save } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { useTranslation } from 'react-i18next';
import { getLocalizedAI } from '@/utils/localizedAI';

const IncidentAIAnalysis = ({ reportId, incidentFormData, existingAnalysis, onAnalysisUpdate, onLinkedDocsUpdate, incidentReport }) => {
  const { t } = useTranslation();
  // Use localized AI analysis if available from the incident report
  const localizedAnalysis = incidentReport ? getLocalizedAI(incidentReport, 'aiAnalysis') : existingAnalysis;
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(localizedAnalysis || existingAnalysis);
  const [linkedJSAs, setLinkedJSAs] = useState([]);
  const [linkedAudits, setLinkedAudits] = useState([]);
  const [editingSection, setEditingSection] = useState(null);
  const [editValue, setEditValue] = useState('');

  // Don't render if no reportId or formData is provided
  if (!reportId && !incidentFormData) {
    console.warn('IncidentAIAnalysis: No reportId or incidentFormData provided, component will not render');
    return null;
  }

  const analyzeIncident = async () => {
    setAnalyzing(true);
    try {
      console.log('IncidentAIAnalysis - reportId:', reportId);
      console.log('IncidentAIAnalysis - incidentFormData:', incidentFormData);
      
      let response;
      if (reportId) {
        // Analyze saved report
        console.log('Analyzing with reportId:', reportId);
        response = await axiosInstance.post(`/incident-reports/${reportId}/ai-analyze`);
      } else if (incidentFormData) {
        // Analyze form data without saving
        console.log('Analyzing with form data');
        response = await axiosInstance.post('/incident-reports/analyze-draft', incidentFormData);
      } else {
        console.error('No reportId or incidentFormData provided!');
        console.error('reportId:', reportId, 'type:', typeof reportId);
        console.error('incidentFormData:', incidentFormData);
        throw new Error('No report ID or form data provided');
      }
      
      setAnalysis(response.data.analysis);
      setLinkedJSAs(response.data.linkedJSAs || []);
      setLinkedAudits(response.data.linkedAudits || []);
      
      onAnalysisUpdate(response.data.analysis);
      if (onLinkedDocsUpdate) {
        onLinkedDocsUpdate(response.data.linkedJSAs || [], response.data.linkedAudits || []);
      }
      
      const linkedCount = (response.data.linkedJSAs?.length || 0) + (response.data.linkedAudits?.length || 0);
      toast.success(response.data.savedToLibrary ? 
        `AI analysis complete! Found ${linkedCount} linked safety documents and saved to library.` : 
        `AI analysis complete! Found ${linkedCount} linked safety documents.`);
    } catch (error) {
      toast.error('Failed to analyze incident: ' + (error.response?.data?.detail || error.message));
    } finally {
      setAnalyzing(false);
    }
  };

  const removeItem = (section, index) => {
    const updated = { ...analysis };
    updated[section].splice(index, 1);
    setAnalysis(updated);
    onAnalysisUpdate(updated);
  };

  const startEdit = (section, index, currentValue) => {
    setEditingSection({ section, index });
    setEditValue(JSON.stringify(currentValue, null, 2));
  };

  const saveEdit = () => {
    try {
      const parsed = JSON.parse(editValue);
      const updated = { ...analysis };
      updated[editingSection.section][editingSection.index] = parsed;
      setAnalysis(updated);
      onAnalysisUpdate(updated);
      setEditingSection(null);
      toast.success('Changes saved');
    } catch (error) {
      toast.error('Invalid JSON format');
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return 'bg-red-100 text-red-800 border-red-300';
      case 'Medium': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'Low': return 'bg-green-100 text-green-800 border-green-300';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Human Error': return '👤';
      case 'Equipment Failure': return '🔧';
      case 'Environmental': return '🌦️';
      case 'Procedural': return '📋';
      case 'Training Gap': return '📚';
      default: return '❓';
    }
  };

  if (!analysis) {
    return (
      <Card className="mt-4">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-600" />
            Safety Analysis
          </CardTitle>
          <CardDescription>
            Get AI-powered root cause analysis and safety recommendations
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button 
            onClick={analyzeIncident} 
            disabled={analyzing}
            className="bg-purple-600 hover:bg-purple-700"
          >
            {analyzing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Analyzing...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Analyze with AI
              </>
            )}
          </Button>
          <p className="text-sm text-gray-600 mt-3">
            AI will analyze this incident and provide:
          </p>
          <ul className="text-sm text-gray-600 ml-4 mt-2 space-y-1 list-disc">
            <li>Root cause analysis</li>
            <li>Related JSA recommendations</li>
            <li>Prevention strategies</li>
            <li>Severity validation</li>
          </ul>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4 mt-4">
      <Card>
        <CardHeader className="bg-purple-50">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              Safety Analysis
            </CardTitle>
            <Button 
              size="sm" 
              variant="outline"
              onClick={analyzeIncident} 
              disabled={analyzing}
            >
              {analyzing ? 'Analyzing...' : 'Re-analyze'}
            </Button>
          </div>
          <CardDescription>Review and edit AI suggestions before submitting</CardDescription>
        </CardHeader>
      </Card>

      {/* Root Causes */}
      <Card>
        <CardHeader className="bg-red-50">
          <CardTitle className="text-red-900 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Root Causes ({analysis.rootCauses?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4 space-y-3">
          {analysis.rootCauses?.map((cause, index) => (
            <div key={index} className="border rounded-lg p-4 bg-white">
              {editingSection?.section === 'rootCauses' && editingSection?.index === index ? (
                <div className="space-y-2">
                  <Textarea
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    rows={6}
                    className="font-mono text-sm"
                  />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={saveEdit}>
                      <Save className="w-3 h-3 mr-1" /> Save
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditingSection(null)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-start justify-between mb-2">
                    <Badge className="bg-red-100 text-red-800">
                      {getCategoryIcon(cause.category)} {cause.category}
                    </Badge>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => startEdit('rootCauses', index, cause)}
                      >
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeItem('rootCauses', index)}
                      >
                        <Trash2 className="w-3 h-3 text-red-600" />
                      </Button>
                    </div>
                  </div>
                  <h4 className="font-semibold text-gray-900">{cause.cause}</h4>
                  <p className="text-sm text-gray-600 mt-1">{cause.explanation}</p>
                </>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Recommendations */}
      <Card>
        <CardHeader className="bg-blue-50">
          <CardTitle className="text-blue-900 flex items-center gap-2">
            <Lightbulb className="w-5 h-5" />
            Safety Recommendations ({analysis.recommendations?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4 space-y-3">
          {analysis.recommendations?.map((rec, index) => (
            <div key={index} className="border rounded-lg p-4 bg-white">
              {editingSection?.section === 'recommendations' && editingSection?.index === index ? (
                <div className="space-y-2">
                  <Textarea
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    rows={6}
                    className="font-mono text-sm"
                  />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={saveEdit}>
                      <Save className="w-3 h-3 mr-1" /> Save
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditingSection(null)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex gap-2">
                      <Badge className={getPriorityColor(rec.priority)}>
                        {rec.priority}
                      </Badge>
                      <Badge variant="outline">{rec.category}</Badge>
                      <Badge variant="outline">{rec.timeline}</Badge>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => startEdit('recommendations', index, rec)}
                      >
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeItem('recommendations', index)}
                      >
                        <Trash2 className="w-3 h-3 text-red-600" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-sm text-gray-900">{rec.action}</p>
                </>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Related JSAs */}
      {analysis.relatedJSAs?.length > 0 && (
        <Card>
          <CardHeader className="bg-green-50">
            <CardTitle className="text-green-900 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5" />
              Related JSAs ({analysis.relatedJSAs.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4 space-y-3">
            {analysis.relatedJSAs.map((jsa, index) => (
              <div key={index} className="border rounded-lg p-4 bg-white">
                <div className="flex items-start justify-between mb-2">
                  <Badge variant={jsa.updateNeeded ? "destructive" : "default"}>
                    {jsa.updateNeeded ? 'Update Needed' : 'Reference Only'}
                  </Badge>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => removeItem('relatedJSAs', index)}
                  >
                    <Trash2 className="w-3 h-3 text-red-600" />
                  </Button>
                </div>
                <h4 className="font-semibold text-gray-900">{jsa.jsaTask}</h4>
                <p className="text-sm text-gray-600 mt-1"><strong>Why relevant:</strong> {jsa.reason}</p>
                {jsa.updateNeeded && jsa.suggestedUpdate && (
                  <p className="text-sm text-orange-700 mt-2 bg-orange-50 p-2 rounded">
                    <strong>Suggested update:</strong> {jsa.suggestedUpdate}
                  </p>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Severity Validation */}
      {analysis.severityValidation && (
        <Card>
          <CardHeader className={`${analysis.severityValidation.escalationNeeded ? 'bg-red-50' : 'bg-gray-50'}`}>
            <CardTitle className="flex items-center gap-2">
              {analysis.severityValidation.escalationNeeded ? (
                <XCircle className="w-5 h-5 text-red-600" />
              ) : (
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              )}
              Severity Validation
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Reported:</span>
                <Badge>{analysis.severityValidation.reportedSeverity}</Badge>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">AI Assessment:</span>
                <Badge className={analysis.severityValidation.escalationNeeded ? 'bg-red-600' : ''}>
                  {analysis.severityValidation.aiAssessedSeverity}
                </Badge>
              </div>
              <p className="text-sm text-gray-600 mt-3">{analysis.severityValidation.reasoning}</p>
              {analysis.severityValidation.escalationNeeded && (
                <div className="bg-red-50 border border-red-200 rounded p-3 mt-3">
                  <p className="text-sm text-red-800 font-medium">
                    ⚠️ Escalation recommended - Consider increasing severity level
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Prevention Strategy */}
      {analysis.preventionStrategy && (
        <Card>
          <CardHeader className="bg-blue-50">
            <CardTitle className="text-blue-900">Prevention Strategy</CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <p className="text-gray-700">{analysis.preventionStrategy}</p>
          </CardContent>
        </Card>
      )}

      {/* Linked JSAs from Location */}
      {linkedJSAs.length > 0 && (
        <Card>
          <CardHeader className="bg-purple-50">
            <CardTitle className="text-purple-900 flex items-center gap-2">
              📋 JSAs at Incident Location ({linkedJSAs.length})
            </CardTitle>
            <CardDescription>
              These JSAs were active at the incident location and provide context for analysis
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-4 space-y-3">
            {linkedJSAs.map((jsa, index) => (
              <div key={index} className="border rounded-lg p-4 bg-purple-50">
                <h4 className="font-semibold text-gray-900">{jsa.task}</h4>
                <p className="text-sm text-gray-600 mt-1">📍 {jsa.location}</p>
                {jsa.hazards && jsa.hazards.length > 0 && (
                  <div className="mt-2">
                    <p className="text-sm font-medium text-gray-700">Identified Hazards:</p>
                    <ul className="text-sm text-gray-600 ml-4 mt-1 list-disc">
                      {jsa.hazards.map((hazard, hi) => (
                        <li key={hi}>{hazard.hazard}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Linked Audits from Location */}
      {linkedAudits.length > 0 && (
        <Card>
          <CardHeader className="bg-indigo-50">
            <CardTitle className="text-indigo-900 flex items-center gap-2">
              🔍 Recent Audits at Location ({linkedAudits.length})
            </CardTitle>
            <CardDescription>
              Safety audits previously conducted at this location
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-4 space-y-3">
            {linkedAudits.map((audit, index) => (
              <div key={index} className="border rounded-lg p-4 bg-indigo-50">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-semibold text-gray-900">
                      Audit: {new Date(audit.auditDate).toLocaleDateString()}
                    </h4>
                    <p className="text-sm text-gray-600">📍 {audit.location}</p>
                  </div>
                  <Badge className={audit.complianceScore >= 80 ? 'bg-green-600' : audit.complianceScore >= 60 ? 'bg-yellow-600' : 'bg-red-600'}>
                    {audit.complianceScore}% Compliance
                  </Badge>
                </div>
                {audit.issues && audit.issues.length > 0 && (
                  <div className="mt-2">
                    <p className="text-sm font-medium text-gray-700">Issues Found:</p>
                    <ul className="text-sm text-gray-600 ml-4 mt-1 list-disc">
                      {audit.issues.map((issue, ii) => (
                        <li key={ii}>{issue.description || issue}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default IncidentAIAnalysis;
